/**
 * Text utilities for formatting and parsing user-generated content
 */

/**
 * Parse and linkify @username mentions in text
 * @param {string} text - Text containing @username mentions
 * @returns {Array} Array of text segments and link objects
 *
 * Example:
 *   linkifyMentions("Paired with @john on API")
 *   => [
 *     { type: 'text', content: 'Paired with ' },
 *     { type: 'mention', username: 'john', content: '@john' },
 *     { type: 'text', content: ' on API' }
 *   ]
 */
export function linkifyMentions(text, options = {}) {
  if (!text) return [{ type: 'text', content: '' }]

  const { allowedUsernames = null } = options
  const normalizedAllowed = allowedUsernames && allowedUsernames.length
    ? new Set(allowedUsernames.map(username => username.toLowerCase()))
    : null

  const mentionRegex = /@([a-zA-Z0-9_\-]+)/g
  const segments = []
  let lastIndex = 0
  let match

  while ((match = mentionRegex.exec(text)) !== null) {
    const matchIndex = match.index
    const charBefore = matchIndex > 0 ? text[matchIndex - 1] : ''

    // Skip matches that are part of emails or other words (e.g. foo@bar)
    if (charBefore && /[a-zA-Z0-9_\-]/.test(charBefore)) {
      continue
    }

    if (matchIndex > lastIndex) {
      segments.push({
        type: 'text',
        content: text.substring(lastIndex, matchIndex)
      })
    }

    const username = match[1]
    const isAllowed = !normalizedAllowed || normalizedAllowed.has(username.toLowerCase())

    if (isAllowed) {
      segments.push({
        type: 'mention',
        username,
        content: match[0]
      })
    } else {
      segments.push({
        type: 'text',
        content: match[0]
      })
    }

    lastIndex = matchIndex + match[0].length
  }

  if (lastIndex < text.length) {
    segments.push({
      type: 'text',
      content: text.substring(lastIndex)
    })
  }

  if (segments.length === 0) {
    segments.push({ type: 'text', content: text })
  }

  return segments
}

export function sanitizeUserMentions(text, allowedUsernames = []) {
  if (!text || !Array.isArray(allowedUsernames) || allowedUsernames.length === 0) {
    return text
  }

  const allowedSet = new Set(allowedUsernames.map(username => username.toLowerCase()))
  const mentionPattern = /@([a-zA-Z0-9_\-]+)(?=[\s,.!?;]|$)/g

  return text.replace(mentionPattern, (match, username) => {
    return allowedSet.has(username.toLowerCase()) ? match : username
  })
}

// Rendering handled by RenderedMentions component to avoid JSX in this utility
